import { ConsoleUserSchema, TOKEN_TTL_SECONDS } from '@paguz/contracts';
import { useSyncExternalStore } from 'react';
import { z } from 'zod';
import { can, type Permission } from './rbac.ts';

/**
 * Sesión de consola. El token vive en `sessionStorage` (se borra al cerrar la pestaña) y nunca
 * en `localStorage`. Vida: `TOKEN_TTL_SECONDS.console` (8 h).
 */
const StoredSessionSchema = z.object({
  accessToken: z.string().min(1),
  user: ConsoleUserSchema,
  loginAt: z.string(),
  expiresAt: z.string(),
});
export type Session = z.infer<typeof StoredSessionSchema>;

const STORAGE_KEY = 'paguz.console.session';

type Listener = () => void;
const listeners = new Set<Listener>();
let current: Session | null = readStored();

function readStored(): Session | null {
  try {
    const raw = globalThis.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = StoredSessionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    return isExpired(parsed.data) ? null : parsed.data;
  } catch {
    return null;
  }
}

function persist(session: Session | null): void {
  try {
    if (session) globalThis.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else globalThis.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Almacenamiento bloqueado (modo privado): la sesión queda solo en memoria.
  }
}

function isExpired(session: Session): boolean {
  return new Date(session.expiresAt).getTime() <= Date.now();
}

function emit(): void {
  for (const listener of listeners) listener();
}

export const sessionStore = {
  get: (): Session | null => {
    if (current && isExpired(current)) {
      current = null;
      persist(null);
    }
    return current;
  },
  start: (accessToken: string, user: Session['user']): Session => {
    const loginAt = new Date();
    current = {
      accessToken,
      user,
      loginAt: loginAt.toISOString(),
      expiresAt: new Date(loginAt.getTime() + TOKEN_TTL_SECONDS.console * 1000).toISOString(),
    };
    persist(current);
    emit();
    return current;
  },
  end: (): void => {
    current = null;
    persist(null);
    emit();
  },
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useSession(): Session | null {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}

/** Permiso del usuario actual (falso sin sesión). */
export function useCan(permission: Permission): boolean {
  const session = useSession();
  return session ? can(session.user.roles, permission) : false;
}
